import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { BookButton } from "@/components/Buttons";
import { SERVICES } from "@/data";
import { useSEO } from "@/hooks/useSEO";

export default function ServicesPage() {
  useSEO({
    title: "Dental Treatments in Canberra City | Glebe Park Dental",
    description:
      "Check-ups, same-day crowns, implants, whitening, clear aligners, root canals, wisdom teeth, kids' dentistry and sedation for anxious patients — all under one roof next to Glebe Park, Canberra City.",
    path: "/services",
  });

  return (
    <main className="bg-cream" data-testid="services-page">
      <Nav />
      <section className="bg-sage pb-20 pt-40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-leafmute">What we do</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              Ten treatments, <span className="italic text-fern">one address.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl font-sans text-sm leading-relaxed text-fern sm:text-base">
              From the six-monthly clean to implants, clear aligners and wisdom teeth, it all happens here on
              Coranderrk Street. Pick a treatment to read the plain-English version of what's
              involved, how long it takes and what it'll cost.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={0.06 * (i % 3)} y={24}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`services-page-card-${s.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-ink/8 bg-white shadow-[0_2px_20px_-8px_rgba(18,26,21,0.1)] transition-[box-shadow,transform] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-16px_rgba(18,26,21,0.25)]"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img src={s.image} alt={s.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    <span className="absolute left-4 top-4 rounded-full bg-forest/80 px-3 py-1 font-mono text-xs tracking-[0.25em] uppercase text-leaf backdrop-blur">
                      {s.tag}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <h2 className="font-serif text-xl leading-snug text-ink transition-colors duration-300 group-hover:text-leafdeep sm:text-2xl">{s.name}</h2>
                    <p className="mt-3 flex-1 font-sans text-sm leading-relaxed text-fern">{s.blurb}</p>
                    <span className="mt-5 inline-flex items-center gap-2 font-sans text-sm font-medium text-ink transition-colors duration-300 group-hover:text-leafdeep">
                      Read more <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-sage py-20">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-5 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:px-16">
          <Reveal>
            <h2 className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">
              Not sure what you need? <span className="italic text-fern">Start with a check-up.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <BookButton testid="services-page-book-button" />
          </Reveal>
        </div>
      </section>
      <Footer />
    </main>
  );
}
